import { SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';
import { SetScheduleStatusActionService } from '~src/schedule/actions/SetScheduleStatusAction';

import { UpdateScheduleRequest } from '../../../backgroundJob/types/UpdateScheduleRequest';
import { Messages } from '../../../codegen/Localize';
import { openReactActionDialog } from '../../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../../commonInfra/dialogs/style/TabDialogWidth';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { WebHttpClientWithErrorToastInstance } from '../../../httpClient/WebHttpClientWithErrorToast';
import { RunSchedulesActionService } from '../../../schedule/actions/RunSchedulesAction';
import { RunServerSchedulesActionService } from '../../../schedule/actions/RunServerSchedulesAction';
import { ScheduleMenuAction } from '../../../schedule/actions/ScheduleMenuActions';
import { ScheduleCreateOrEditActionBody, ScheduleCreateOrEditActionBodyProps, StateForScheduleCreateOrEditActionBody } from '../../../schedule/components/ScheduleCreateEditActionBody';
import { ScheduleAction } from '../../../schedule/types/ISchedule';
import { IScheduleInfo } from '../../../schedule/types/IScheduleInfo';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { Set } from '../../../ts/models/Set';
import { CreateOrUpdateScheduleActionService } from '../../../ts/services/CreateOrUpdateScheduleAction';
import { ScheduleDisable, ScheduleDisableLinkedTask,ScheduleEdit, ScheduleEnable, ScheduleEnableLinkedTask, ScheduleRunNow } from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { NavLocationSelectors } from '../../selectors/NavigationStateSelectors';

export function runServerSchedules(schedules: IScheduleInfo[]) {
  return RunServerSchedulesActionService.execute(schedules);
}

export function runSiteSchedules(schedules: IScheduleInfo[]) {
  return RunSchedulesActionService.execute(schedules);
}

export function* handleRunNow(action: ScheduleMenuAction): SagaIterator {
  try {
    const inServerArea = yield select(NavLocationSelectors.inServerArea);
    if (inServerArea) {
      yield call(runServerSchedules, action.items);
    } else {
      yield call(runSiteSchedules, action.items);
    }

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function enable(schedules: IScheduleInfo[]) {
  return SetScheduleStatusActionService.confirmEnableSchedules(Set.fromIdItemArray(schedules));
}

export function* handleEnable(action: ScheduleMenuAction): SagaIterator {
  try {
    yield call(enable, action.items);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function disable(schedules: IScheduleInfo[]) {
  return SetScheduleStatusActionService.confirmDisableSchedules(Set.fromIdItemArray(schedules));
}

export function* handleDisable(action: ScheduleMenuAction): SagaIterator {
  try {
    yield call(disable, action.items);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function editSelfServiceSchedule(schedule: IScheduleInfo, isNameEditingDisabled?: boolean): Promise<IResult> {
  const recurrence = IScheduleInfo.toSSSRecurrence(schedule);
  const initialDialogState: StateForScheduleCreateOrEditActionBody = {
    id: schedule.id,
    name: schedule.name,
    scheduledAction: schedule.scheduledAction as ScheduleAction,
    priority: schedule.priority,
    parallel: schedule.parallel,
    recurrence: recurrence,
    enabledForLinkedTasks: schedule.enabledForLinkedTasks,
  };
  const initialBodyProps = {
    dialogMode: SelfServiceSchedulePaneType.AdminSchedule,
    isNew: false,
    isNameEditingDisabled: isNameEditingDisabled,
  };
  const action = (props: ScheduleCreateOrEditActionBodyProps, state: StateForScheduleCreateOrEditActionBody) => {
    const request = new UpdateScheduleRequest({
      name: state.name,
      scheduleType: state.recurrence.recurrenceType,
      scheduleDetails: state.recurrence.recurrenceDetails,
      priority: state.priority,
      parallel: state.parallel,
      id: state.id,
      enabledForLinkedTasks: state.enabledForLinkedTasks
    });
    return WebHttpClientWithErrorToastInstance.sendRequest(request).then(result => {
      CreateOrUpdateScheduleActionService.notifyResult(result, state.name, state.name);
      return result;
    });
  };
  const modalInstance = openReactActionDialog({
    title: Messages.adminSelfServiceSchedulesDialogEditTitle(),
    bodyComponent: ScheduleCreateOrEditActionBody,
    initialBodyProps: initialBodyProps,
    initialDialogState: initialDialogState,
    actionName: Messages.adminSelfServiceSchedulesDialogConfirmUpdate(),
    action: action,
    actionDisabled: (props, state) => state.name.length === 0,
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
  });
  return modalInstance.result;
}

export function edit(schedules: IScheduleInfo[]) {
  const schedule = schedules[0];
  if (FeatureFlags.adminSelfServiceSchedulesEnabled) {
    return editSelfServiceSchedule(schedule);
  } else {
    return CreateOrUpdateScheduleActionService.editSchedule(schedule);
  }
}

export function* handleEdit(action: ScheduleMenuAction): SagaIterator {
  try {
    if (action.items.length > 0) {
      yield call(edit, action.items);
    }

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function enableLinkedTasks(schedules: IScheduleInfo[]) {
  return SetScheduleStatusActionService.confirmEnableLinkedTaskSchedules(Set.fromIdItemArray(schedules));
}

export function* handleEnableLinkedTasks(action: ScheduleMenuAction): SagaIterator {
  if (areLinkedTasksEnabled()) {
    try {
      yield call(enableLinkedTasks, action.items);
      yield put(requestSpaceContents());
    } catch (e) {
      yield put(requestFailed(e, Messages.errorUnknown()));
    }
  }
}

export function disableLinkedTasks(schedules: IScheduleInfo[]) {
  return SetScheduleStatusActionService.confirmDisableLinkedTaskSchedules(Set.fromIdItemArray(schedules));
}

export function* handleDisableLinkedTasks(action: ScheduleMenuAction): SagaIterator {
  if (areLinkedTasksEnabled()) {
    try {
      yield call(disableLinkedTasks, action.items);
      yield put(requestSpaceContents());
    } catch (e) {
      yield put(requestFailed(e, Messages.errorUnknown()));
    }
  }
}

export function* rootScheduleMenuActionSaga(): SagaIterator {
  yield takeEvery(ScheduleRunNow, handleRunNow);
  yield takeEvery(ScheduleEnable, handleEnable);
  yield takeEvery(ScheduleDisable, handleDisable);
  yield takeEvery(ScheduleEdit, handleEdit);
  yield takeEvery(ScheduleEnableLinkedTask, handleEnableLinkedTasks);
  yield takeEvery(ScheduleDisableLinkedTask, handleDisableLinkedTasks);
}
